import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { User, Position, Operation } from "@/types";
import { logger } from "@/utils";

type VectorClock = Record<string, number>;

export interface UserSession {
  userId: string;
  user: User;
  cursor?: Position;
  selection?: string[];
  currentAction?: "dragging" | "editing" | "idle";
  draggedComponent?: string;
  color: string; // Unique color for this user
  lastActivity: Date;
  isOnline: boolean;
  socketId?: string;
}

// Kept for backward compatibility - drag functionality removed
export interface DragConflict {
  id: string;
  componentId: string;
  users: UserSession[];
  initiatedBy: string;
  timestamp: Date;
  resolvedBy?: string;
  resolution?: "merge" | "reject" | "queue";
  status: "active" | "resolved" | "timeout";
}


export interface ConflictChoice {
  id: string;
  type: "automatic" | "user_choice" | "manual_merge";
  conflictId: string;
  description: string;
  options?: {
    label: string;
    action: () => void;
    variant: "primary" | "secondary" | "destructive";
  }[];
  timestamp: Date;
  autoResolveIn?: number; // seconds
}

export interface OperationState {
  operation: Operation;
  status: "pending" | "acknowledged" | "failed";
  timestamp: Date;
  retryCount: number;
  transformedOperation?: Operation;
}

export interface CollaborationState {
  // Session management
  sessionId: string | null;
  projectId: string | null;
  isActive: boolean;

  // Users and presence
  currentUser: UserSession | null;
  activeUsers: Map<string, UserSession>;
  userPresence: Map<string, "online" | "idle" | "offline">;

  // Conflicts and resolution
  dragConflicts: Map<string, DragConflict>;
  conflictChoices: ConflictChoice[];
  lockingEnabled: boolean;
  componentLocks: Map<string, string>; // componentId -> userId

  // Operations and sync
  pendingOperations: Map<string, OperationState>;
  vectorClock: VectorClock;
  lastSyncTime: Date | null;
  operationQueue: Operation[];

  // Performance and optimization
  throttleSettings: {
    cursorUpdateDelay: number;
    presenceUpdateDelay: number;
    operationBatchSize: number;
    operationBatchDelay: number;
  };

  // Mobile optimizations
  isMobileOptimized: boolean;
  batteryOptimization: {
    enabled: boolean;
    reducedUpdates: boolean;
    backgroundSync: boolean;
  };

  // Accessibility
  accessibility: {
    announceUserActions: boolean;
    highlightConflicts: boolean;
    keyboardNavigation: boolean;
  };

  // Statistics
  stats: {
    operationsSent: number;
    operationsReceived: number;
    conflictsResolved: number;
    averageLatency: number;
  };
}

interface CollaborationStore extends CollaborationState {
  // Session actions
  initializeSession: (
    sessionId: string,
    projectId: string,
    currentUser: User,
  ) => void;
  terminateSession: () => void;

  // User management
  addUser: (userSession: UserSession) => void;
  removeUser: (userId: string) => void;
  updateUserCursor: (userId: string, cursor: Position) => void;
  updateUserSelection: (userId: string, selection: string[]) => void;
  updateUserAction: (
    userId: string,
    action: "dragging" | "editing" | "idle",
    componentId?: string,
  ) => void;
  updateUserPresence: (
    userId: string,
    presence: "online" | "idle" | "offline",
  ) => void;

  // Conflict management
  createDragConflict: (componentId: string, users: string[]) => string;
  resolveDragConflict: (
    conflictId: string,
    resolution: "merge" | "reject" | "queue",
    resolvedBy?: string,
  ) => void;
  addConflictChoice: (choice: Omit<ConflictChoice, "id" | "timestamp">) => void;
  resolveConflictChoice: (choiceId: string) => void;
  clearExpiredConflicts: () => void;

  // Component locking
  requestComponentLock: (componentId: string, userId: string) => boolean;
  releaseComponentLock: (componentId: string, userId?: string) => void;
  isComponentLocked: (componentId: string) => boolean;
  getComponentLockOwner: (componentId: string) => UserSession | null;

  // Operation management
  addPendingOperation: (operation: Operation) => void;
  acknowledgeOperation: (
    operationId: string,
    transformedOperation?: Operation,
  ) => void;
  failOperation: (operationId: string) => void;
  retryFailedOperations: () => void;
  clearOldOperations: () => void;

  // Vector clock
  updateVectorClock: (vectorClock: VectorClock) => void;
  incrementVectorClock: (userId: string) => VectorClock;

  // Sync management
  updateLastSyncTime: () => void;
  needsSync: () => boolean;

  // Settings and optimization
  updateThrottleSettings: (
    settings: Partial<CollaborationState["throttleSettings"]>,
  ) => void;
  toggleMobileOptimization: (enabled: boolean) => void;
  updateBatteryOptimization: (
    settings: Partial<CollaborationState["batteryOptimization"]>,
  ) => void;
  updateAccessibilitySettings: (
    settings: Partial<CollaborationState["accessibility"]>,
  ) => void;

  // Statistics
  incrementOperationsSent: () => void;
  incrementOperationsReceived: () => void;
  incrementConflictsResolved: () => void;
  updateAverageLatency: (latency: number) => void;
  getStats: () => CollaborationState["stats"];

  // Utility methods
  getCurrentUser: () => UserSession | null;
  getActiveUsers: () => UserSession[];
  getUserBySocketId: (socketId: string) => UserSession | null;
  getConflictsForComponent: (componentId: string) => DragConflict[];
  hasActiveConflicts: () => boolean;

  // Cleanup methods
  cleanup: () => void;
  reset: () => void;
}

// Generate consistent colors for users
const generateUserColor = (userId: string): string => {
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E9",
    "#F8B4CB",
    "#A8E6CF",
    "#FFD93D",
    "#FF8A80",
    "#81C784",
  ];

  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

// Generate unique ID
const generateId = (prefix: string = "id"): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

export const useCollaborationStore = create<CollaborationStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      sessionId: null,
      projectId: null,
      isActive: false,

      currentUser: null,
      activeUsers: new Map(),
      userPresence: new Map(),

      dragConflicts: new Map(),
      conflictChoices: [],
      lockingEnabled: true,
      componentLocks: new Map(),

      pendingOperations: new Map(),
      vectorClock: {},
      lastSyncTime: null,
      operationQueue: [],

      throttleSettings: {
        cursorUpdateDelay: 60, // 60ms for optimal 16fps
        presenceUpdateDelay: 1000,
        operationBatchSize: 10,
        operationBatchDelay: 100,
      },

      isMobileOptimized: false,
      batteryOptimization: {
        enabled: false,
        reducedUpdates: false,
        backgroundSync: true,
      },

      accessibility: {
        announceUserActions: true,
        highlightConflicts: true,
        keyboardNavigation: true,
      },

      stats: {
        operationsSent: 0,
        operationsReceived: 0,
        conflictsResolved: 0,
        averageLatency: 0,
      },

      // Session actions
      initializeSession: (
        sessionId: string,
        projectId: string,
        currentUser: User,
      ) => {
        set((state) => {
          state.sessionId = sessionId;
          state.projectId = projectId;
          state.isActive = true;
          state.vectorClock = { [currentUser.id]: 0 };
          state.lastSyncTime = new Date();

          const userSession: UserSession = {
            userId: currentUser.id,
            user: currentUser,
            currentAction: "idle",
            color: generateUserColor(currentUser.id),
            lastActivity: new Date(),
            isOnline: true,
          };

          state.currentUser = userSession;
          state.activeUsers.set(currentUser.id, userSession);
          state.userPresence.set(currentUser.id, "online");
        });

        logger.info("Collaboration session initialized", {
          sessionId,
          projectId,
        });
      },

      terminateSession: () => {
        set((state) => {
          state.sessionId = null;
          state.projectId = null;
          state.isActive = false;
          state.currentUser = null;
          state.activeUsers.clear();
          state.userPresence.clear();
          state.dragConflicts.clear();
          state.conflictChoices = [];
          state.componentLocks.clear();
          state.pendingOperations.clear();
          state.vectorClock = {};
          state.operationQueue = [];
          state.lastSyncTime = null;
        });

        logger.info("Collaboration session terminated");
      },

      // User management
      addUser: (userSession: UserSession) => {
        set((state) => {
          state.activeUsers.set(userSession.userId, userSession);
          state.userPresence.set(userSession.userId, "online");
        });

        logger.info("User joined collaboration", {
          userId: userSession.userId,
        });
      },

      removeUser: (userId: string) => {
        set((state) => {
          state.activeUsers.delete(userId);
          state.userPresence.delete(userId);

          // Release any locks held by the leaving user
          for (const [
            componentId,
            lockOwner,
          ] of state.componentLocks.entries()) {
            if (lockOwner === userId) {
              state.componentLocks.delete(componentId);
            }
          }

          // Remove from active conflicts
          for (const [conflictId, conflict] of state.dragConflicts.entries()) {
            conflict.users = conflict.users.filter((u) => u.userId !== userId);
            if (conflict.users.length <= 1) {
              state.dragConflicts.delete(conflictId);
            }
          }
        });

        logger.info("User left collaboration", { userId });
      },

      updateUserCursor: (userId: string, cursor: Position) => {
        set((state) => {
          const user = state.activeUsers.get(userId);
          if (user) {
            user.cursor = cursor;
            user.lastActivity = new Date();
            state.activeUsers.set(userId, user);
          }
        });
      },

      updateUserSelection: (userId: string, selection: string[]) => {
        set((state) => {
          const user = state.activeUsers.get(userId);
          if (user) {
            user.selection = selection;
            user.lastActivity = new Date();
            state.activeUsers.set(userId, user);
          }
        });
      },

      updateUserAction: (
        userId: string,
        action: "dragging" | "editing" | "idle",
        componentId?: string,
      ) => {
        set((state) => {
          const user = state.activeUsers.get(userId);
          if (user) {
            user.currentAction = action;
            user.draggedComponent =
              action === "dragging" ? componentId : undefined;
            user.lastActivity = new Date();
            state.activeUsers.set(userId, user);
          }
        });
      },

      updateUserPresence: (
        userId: string,
        presence: "online" | "idle" | "offline",
      ) => {
        set((state) => {
          state.userPresence.set(userId, presence);

          const user = state.activeUsers.get(userId);
          if (user) {
            user.isOnline = presence !== "offline";
            if (presence === "offline") {
              user.cursor = undefined;
              user.selection = [];
              user.currentAction = "idle";
            }
            state.activeUsers.set(userId, user);
          }
        });
      },

      // Conflict management
      createDragConflict: (componentId: string, userIds: string[]) => {
        const conflictId = generateId("conflict");
        const users = userIds
          .map((id) => get().activeUsers.get(id))
          .filter(Boolean) as UserSession[];

        set((state) => {
          const conflict: DragConflict = {
            id: conflictId,
            componentId,
            users,
            initiatedBy: users[0]?.userId || "unknown",
            timestamp: new Date(),
            status: "active",
          };

          state.dragConflicts.set(conflictId, conflict);

          // Create conflict choice for automatic resolution (90% confidence)
          const choice: ConflictChoice = {
            id: generateId("choice"),
            type: "automatic",
            conflictId,
            description: `Multiple users are editing component ${componentId}`,
            autoResolveIn: 5, // 5 seconds
            timestamp: new Date(),
          };

          state.conflictChoices.push(choice);
        });

        logger.warn("Drag conflict created", {
          conflictId,
          componentId,
          userIds,
        });
        return conflictId;
      },

      resolveDragConflict: (
        conflictId: string,
        resolution: "merge" | "reject" | "queue",
        resolvedBy?: string,
      ) => {
        set((state) => {
          const conflict = state.dragConflicts.get(conflictId);
          if (conflict) {
            conflict.resolution = resolution;
            conflict.resolvedBy = resolvedBy;
            conflict.status = "resolved";
            state.dragConflicts.set(conflictId, conflict);

            // Remove related conflict choices
            state.conflictChoices = state.conflictChoices.filter(
              (choice) => choice.conflictId !== conflictId,
            );

            // Increment stats
            state.stats.conflictsResolved++;
          }
        });

        logger.info("Drag conflict resolved", {
          conflictId,
          resolution,
          resolvedBy,
        });
      },

      addConflictChoice: (choice: Omit<ConflictChoice, "id" | "timestamp">) => {
        set((state) => {
          const newChoice: ConflictChoice = {
            ...choice,
            id: generateId("choice"),
            timestamp: new Date(),
          };

          state.conflictChoices.push(newChoice);
        });
      },

      resolveConflictChoice: (choiceId: string) => {
        set((state) => {
          state.conflictChoices = state.conflictChoices.filter(
            (choice) => choice.id !== choiceId,
          );
        });
      },

      clearExpiredConflicts: () => {
        set((state) => {
          const now = Date.now();
          const expiredTime = 5 * 60 * 1000; // 5 minutes

          // Clear expired conflicts
          for (const [id, conflict] of state.dragConflicts.entries()) {
            if (now - conflict.timestamp.getTime() > expiredTime) {
              state.dragConflicts.delete(id);
            }
          }

          // Clear expired choices
          state.conflictChoices = state.conflictChoices.filter((choice) => {
            if (choice.autoResolveIn) {
              return (
                now - choice.timestamp.getTime() < choice.autoResolveIn * 1000
              );
            }
            return now - choice.timestamp.getTime() < expiredTime;
          });
        });
      },

      // Component locking
      requestComponentLock: (componentId: string, userId: string): boolean => {
        const state = get();

        if (!state.lockingEnabled) return true;

        const currentLockOwner = state.componentLocks.get(componentId);
        if (currentLockOwner && currentLockOwner !== userId) {
          return false; // Already locked by someone else
        }

        set((draft) => {
          draft.componentLocks.set(componentId, userId);
        });

        return true;
      },

      releaseComponentLock: (componentId: string, userId?: string) => {
        set((state) => {
          const lockOwner = state.componentLocks.get(componentId);
          if (!userId || lockOwner === userId) {
            state.componentLocks.delete(componentId);
          }
        });
      },

      isComponentLocked: (componentId: string): boolean => {
        const state = get();
        const lockOwner = state.componentLocks.get(componentId);
        return (
          lockOwner !== undefined && lockOwner !== state.currentUser?.userId
        );
      },

      getComponentLockOwner: (componentId: string): UserSession | null => {
        const state = get();
        const lockOwnerId = state.componentLocks.get(componentId);
        if (!lockOwnerId || lockOwnerId === state.currentUser?.userId)
          return null;

        return state.activeUsers.get(lockOwnerId) || null;
      },

      // Operation management
      addPendingOperation: (operation: Operation) => {
        set((state) => {
          const operationState: OperationState = {
            operation,
            status: "pending",
            timestamp: new Date(),
            retryCount: 0,
          };

          state.pendingOperations.set(operation.id, operationState);
          state.operationQueue.push(operation);
          state.stats.operationsSent++;
        });
      },

      acknowledgeOperation: (
        operationId: string,
        transformedOperation?: Operation,
      ) => {
        set((state) => {
          const operationState = state.pendingOperations.get(operationId);
          if (operationState) {
            operationState.status = "acknowledged";
            operationState.transformedOperation = transformedOperation;
            state.pendingOperations.set(operationId, operationState);
          }
        });
      },

      failOperation: (operationId: string) => {
        set((state) => {
          const operationState = state.pendingOperations.get(operationId);
          if (operationState) {
            operationState.status = "failed";
            operationState.retryCount++;
            state.pendingOperations.set(operationId, operationState);
          }
        });
      },

      retryFailedOperations: () => {
        const state = get();
        const failedOperations = Array.from(
          state.pendingOperations.values(),
        ).filter((op) => op.status === "failed" && op.retryCount < 3);

        // This would trigger retry logic in the WebSocket service
        logger.info("Retrying failed operations", {
          count: failedOperations.length,
        });
      },

      clearOldOperations: () => {
        set((state) => {
          const now = Date.now();
          const maxAge = 10 * 60 * 1000; // 10 minutes

          for (const [
            id,
            operationState,
          ] of state.pendingOperations.entries()) {
            if (now - operationState.timestamp.getTime() > maxAge) {
              state.pendingOperations.delete(id);
            }
          }

          // Keep operation queue manageable
          if (state.operationQueue.length > 100) {
            state.operationQueue = state.operationQueue.slice(-50);
          }
        });
      },

      // Vector clock
      updateVectorClock: (vectorClock: VectorClock) => {
        set((state) => {
          state.vectorClock = { ...vectorClock };
        });
      },

      incrementVectorClock: (userId: string): VectorClock => {
        const state = get();
        const newClock = { ...state.vectorClock };
        newClock[userId] = (newClock[userId] || 0) + 1;

        set((draft) => {
          draft.vectorClock = newClock;
        });

        return newClock;
      },

      // Sync management
      updateLastSyncTime: () => {
        set((state) => {
          state.lastSyncTime = new Date();
        });
      },

      needsSync: (): boolean => {
        const state = get();
        if (!state.lastSyncTime) return true;

        const timeSinceSync = Date.now() - state.lastSyncTime.getTime();
        return timeSinceSync > 30 * 1000; // 30 seconds
      },

      // Settings and optimization
      updateThrottleSettings: (
        settings: Partial<CollaborationState["throttleSettings"]>,
      ) => {
        set((state) => {
          state.throttleSettings = { ...state.throttleSettings, ...settings };
        });
      },

      toggleMobileOptimization: (enabled: boolean) => {
        set((state) => {
          state.isMobileOptimized = enabled;
          if (enabled) {
            // Adjust throttle settings for mobile
            state.throttleSettings.cursorUpdateDelay = 120; // Slower updates
            state.throttleSettings.presenceUpdateDelay = 2000;
            state.batteryOptimization.enabled = true;
          } else {
            // Reset to desktop settings
            state.throttleSettings.cursorUpdateDelay = 60;
            state.throttleSettings.presenceUpdateDelay = 1000;
          }
        });
      },

      updateBatteryOptimization: (
        settings: Partial<CollaborationState["batteryOptimization"]>,
      ) => {
        set((state) => {
          state.batteryOptimization = {
            ...state.batteryOptimization,
            ...settings,
          };
        });
      },

      updateAccessibilitySettings: (
        settings: Partial<CollaborationState["accessibility"]>,
      ) => {
        set((state) => {
          state.accessibility = { ...state.accessibility, ...settings };
        });
      },

      // Statistics
      incrementOperationsSent: () => {
        set((state) => {
          state.stats.operationsSent++;
        });
      },

      incrementOperationsReceived: () => {
        set((state) => {
          state.stats.operationsReceived++;
        });
      },

      incrementConflictsResolved: () => {
        set((state) => {
          state.stats.conflictsResolved++;
        });
      },

      updateAverageLatency: (latency: number) => {
        set((state) => {
          const currentAvg = state.stats.averageLatency;
          const totalOps =
            state.stats.operationsSent + state.stats.operationsReceived;
          state.stats.averageLatency =
            totalOps > 1
              ? (currentAvg * (totalOps - 1) + latency) / totalOps
              : latency;
        });
      },

      getStats: () => get().stats,

      // Utility methods
      getCurrentUser: () => get().currentUser,

      getActiveUsers: () => Array.from(get().activeUsers.values()),

      getUserBySocketId: (socketId: string) => {
        const users = Array.from(get().activeUsers.values());
        return users.find((user) => user.socketId === socketId) || null;
      },

      getConflictsForComponent: (componentId: string) => {
        return Array.from(get().dragConflicts.values()).filter(
          (conflict) =>
            conflict.componentId === componentId &&
            conflict.status === "active",
        );
      },

      hasActiveConflicts: () => {
        return Array.from(get().dragConflicts.values()).some(
          (conflict) => conflict.status === "active",
        );
      },

      // Cleanup methods
      cleanup: () => {
        const { clearExpiredConflicts, clearOldOperations } = get();
        clearExpiredConflicts();
        clearOldOperations();
      },

      reset: () => {
        set((state) => {
          Object.assign(state, {
            sessionId: null,
            projectId: null,
            isActive: false,
            currentUser: null,
            activeUsers: new Map(),
            userPresence: new Map(),
            dragConflicts: new Map(),
            conflictChoices: [],
            componentLocks: new Map(),
            pendingOperations: new Map(),
            vectorClock: {},
            lastSyncTime: null,
            operationQueue: [],
            stats: {
              operationsSent: 0,
              operationsReceived: 0,
              conflictsResolved: 0,
              averageLatency: 0,
            },
          });
        });
      },
    })),
  ),
);

// Periodic cleanup
setInterval(() => {
  useCollaborationStore.getState().cleanup();
}, 60000); // Every minute
